import { useEffect } from "react";
import { API, api } from "@/lib/api";
import { isStaff } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";

const TARGET_EMAIL = (process.env.REACT_APP_DEVELOPER_MONITOR_EMAIL || "radconsultant315@gmail.com").toLowerCase();
const HEARTBEAT_MS = 30_000;
const HEARTBEAT_MAX_BACKOFF_MS = 5 * 60_000;
const PENDING_TELEMETRY_KEY = "ww.developerMonitor.pending.v1";
const EVENT_KINDS = new Set([
    "console_error", "console_warn", "window_error", "unhandled_rejection", "api_error", "long_task",
    "controller_status", "capture_status", "capture_health", "upload_status", "upload_attempt",
    "ai_clip_decision", "app_telemetry",
]);

const makeId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

function safeText(value) {
    let text;
    try {
        text = typeof value === "string" ? value : JSON.stringify(value);
    } catch {
        text = String(value);
    }
    return String(text || "")
        .replace(/bearer\s+[a-z0-9._~-]+/gi, "[REDACTED]")
        .replace(/(?:password|token|secret|authorization|cookie)\s*[:=]\s*\S+/gi, "[REDACTED]")
        .replace(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, "[REDACTED]")
        .slice(0, 1600);
}

/**
 * Captures operational telemetry from this WhiteWater tab only while the
 * configured developer account or a staff account is signed in.
 */
export default function DeveloperMonitor() {
    const { user } = useAuth();
    const eligible = !!user && (isStaff(user) || user.email?.toLowerCase() === TARGET_EMAIL);

    useEffect(() => {
        if (!eligible) return undefined;

        const sessionId = makeId();
        const tabId = makeId();
        let queue = [];
        try {
            const saved = JSON.parse(window.localStorage.getItem(PENDING_TELEMETRY_KEY) || "[]");
            if (Array.isArray(saved)) queue = saved.slice(-100);
        } catch { queue = []; }
        let stopped = false;
        let heartbeatTimer = null;
        let heartbeatInFlight = false;
        let heartbeatFailures = 0;
        const originalError = console.error;
        const originalWarn = console.warn;
        const navigation = performance.getEntriesByType?.("navigation")?.[0];

        const persistQueue = () => {
            try {
                if (queue.length) window.localStorage.setItem(PENDING_TELEMETRY_KEY, JSON.stringify(queue.slice(-100)));
                else window.localStorage.removeItem(PENDING_TELEMETRY_KEY);
            } catch { /* telemetry persistence is best effort */ }
        };

        const bounded = (value, min, max) => {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : 0;
        };

        const normalizeEvent = (event) => {
            const requestedKind = safeText(event.kind || "app_telemetry");
            const requestedLevel = safeText(event.level || "info");
            return {
                path: safeText(event.path || window.location.pathname).slice(0, 300),
                occurred_at: safeText(event.occurred_at || new Date().toISOString()).slice(0, 60),
                kind: EVENT_KINDS.has(requestedKind) ? requestedKind : "app_telemetry",
                level: ["info", "warning", "error"].includes(requestedLevel) ? requestedLevel : "info",
                message: safeText(event.message),
                stack: safeText(event.stack || "").slice(0, 3000),
                status: Math.round(bounded(event.status, 0, 599)),
                duration_ms: bounded(event.duration_ms, 0, 86_400_000),
                capture_session_id: safeText(event.capture_session_id || "").slice(0, 80),
                artifact_id: safeText(event.artifact_id || "").slice(0, 80),
                bytes: Math.round(bounded(event.bytes, 0, 1_000_000_000)),
                attempt_count: Math.round(bounded(event.attempt_count, 0, 20)),
            };
        };

        const enqueue = (event) => {
            if (queue.length >= 100) queue.shift();
            queue.push(normalizeEvent(event));
            persistQueue();
        };

        const flush = async () => {
            if (!queue.length || stopped) return;
            const events = queue.slice(0, 20).map(normalizeEvent);
            try {
                await api.post("/developer-monitor/events", { session_id: sessionId, tab_id: tabId, events });
                queue.splice(0, events.length);
                persistQueue();
            } catch (error) {
                if (Number(error?.response?.status || 0) === 422) {
                    // Do not let one malformed legacy event permanently block
                    // every newer event behind it. Preserve the field-level
                    // validation detail as a normalized diagnostic instead.
                    queue.splice(0, events.length, normalizeEvent({
                        kind: "app_telemetry",
                        level: "error",
                        message: `Telemetry batch rejected: ${safeText(error?.response?.data?.detail)}`,
                        status: 422,
                    }));
                }
                persistQueue();
            }
        };

        const scheduleHeartbeat = (delay = HEARTBEAT_MS) => {
            if (stopped) return;
            if (heartbeatTimer) window.clearTimeout(heartbeatTimer);
            heartbeatTimer = window.setTimeout(heartbeat, Math.max(1000, delay));
        };

        const heartbeat = async () => {
            if (stopped || heartbeatInFlight) return;
            heartbeatInFlight = true;
            try {
                const response = await api.post("/developer-monitor/heartbeat", {
                    session_id: sessionId,
                    tab_id: tabId,
                    path: window.location.pathname,
                    visibility: document.visibilityState,
                    navigation_ms: navigation?.duration || null,
                });
                heartbeatFailures = 0;
                scheduleHeartbeat(Number(response.data?.heartbeat_seconds || 20) * 1000);
            } catch (error) {
                // The monitor fails open: application behavior remains unchanged.
                heartbeatFailures += 1;
                const retryAfterSeconds = Number(error?.response?.headers?.["retry-after"] || 0);
                const exponential = Math.min(
                    HEARTBEAT_MAX_BACKOFF_MS,
                    HEARTBEAT_MS * (2 ** Math.min(heartbeatFailures, 4)),
                );
                const jitter = Math.round(Math.random() * 2500);
                scheduleHeartbeat(Math.max(retryAfterSeconds * 1000, exponential) + jitter);
            } finally {
                heartbeatInFlight = false;
            }
        };

        console.error = (...args) => {
            originalError(...args);
            enqueue({ kind: "console_error", level: "error", message: args.map(safeText).join(" ") });
        };
        console.warn = (...args) => {
            originalWarn(...args);
            enqueue({ kind: "console_warn", level: "warning", message: args.map(safeText).join(" ") });
        };

        const onWindowError = (event) => enqueue({
            kind: "window_error", level: "error", message: event.message || "Window error",
            stack: event.error?.stack || "",
        });
        const onRejection = (event) => enqueue({
            kind: "unhandled_rejection", level: "error", message: event.reason?.message || event.reason || "Unhandled rejection",
            stack: event.reason?.stack || "",
        });
        const onTelemetry = (event) => {
            const detail = event.detail || {};
            enqueue({
                kind: detail.kind || "app_telemetry",
                level: detail.level || "info",
                message: safeText(detail.message || "Application telemetry"),
                status: Number(detail.status || 0),
                duration_ms: Number(detail.duration_ms || 0),
                capture_session_id: safeText(detail.capture_session_id || ""),
                artifact_id: safeText(detail.artifact_id || ""),
                bytes: Number(detail.bytes || 0),
                attempt_count: Number(detail.attempt_count || 0),
            });
            if (/upload completed|upload failed|capture stopped/i.test(String(detail.message || ""))) {
                queueMicrotask(() => flush());
            }
        };
        const onSession = (event) => {
            const detail = event.detail || {};
            enqueue({
                kind: "capture_status",
                level: detail.monitoring === false && (!detail.controller_saved || !detail.recording_saved) ? "error" : "info",
                message: detail.monitoring === false
                    ? `Live monitoring stopped; controller_saved ${Boolean(detail.controller_saved)}; ` +
                        `recording_saved ${Boolean(detail.recording_saved)}; match_status ${safeText(detail.match_status || "unknown")}; ` +
                        `capture_session ${safeText(detail.capture_session_id || "unknown")}`
                    : `Live monitoring started; game ${safeText(detail.game || "unknown")}`,
                capture_session_id: safeText(detail.capture_session_id || ""),
            });
            queueMicrotask(() => flush());
        };
        window.addEventListener("error", onWindowError);
        window.addEventListener("unhandledrejection", onRejection);
        window.addEventListener("ww:telemetry", onTelemetry);
        window.addEventListener("ww:session", onSession);

        const responseInterceptor = api.interceptors.response.use(
            (response) => response,
            (error) => {
                const url = String(error.config?.url || "");
                if (!url.includes("/developer-monitor/")) {
                    enqueue({
                        kind: "api_error", level: "error",
                        message: `${String(error.config?.method || "GET").toUpperCase()} ${url.split("?")[0]} failed`,
                        status: error.response?.status || 0,
                    });
                }
                return Promise.reject(error);
            },
        );

        let observer;
        try {
            observer = new PerformanceObserver((list) => {
                list.getEntries().filter((entry) => entry.duration >= 250).forEach((entry) => enqueue({
                    kind: "long_task", level: "warning", message: "Browser main thread was blocked",
                    duration_ms: Math.round(entry.duration),
                }));
            });
            observer.observe({ type: "longtask", buffered: true });
        } catch {
            observer = null;
        }

        const stop = () => {
            if (stopped) return;
            const finalEvents = queue.slice(Math.max(0, queue.length - 20)).map(normalizeEvent);
            persistQueue();
            stopped = true;
            const payload = JSON.stringify({ session_id: sessionId, tab_id: tabId, events: finalEvents });
            fetch(`${API}/developer-monitor/stop`, {
                method: "POST", credentials: "include", keepalive: true,
                headers: { "Content-Type": "application/json" }, body: payload,
            }).then((response) => {
                if (response.ok) {
                    queue = [];
                    persistQueue();
                }
            }).catch(() => {});
        };

        heartbeat();
        const flushTimer = window.setInterval(flush, 10_000);
        window.addEventListener("pagehide", stop);

        return () => {
            if (heartbeatTimer) window.clearTimeout(heartbeatTimer);
            window.clearInterval(flushTimer);
            window.removeEventListener("error", onWindowError);
            window.removeEventListener("unhandledrejection", onRejection);
            window.removeEventListener("ww:telemetry", onTelemetry);
            window.removeEventListener("ww:session", onSession);
            window.removeEventListener("pagehide", stop);
            api.interceptors.response.eject(responseInterceptor);
            observer?.disconnect();
            console.error = originalError;
            console.warn = originalWarn;
            stop();
        };
    }, [eligible, user?.email, user?.id, user?.role]);

    return null;
}
